import { useState, useCallback } from 'react';
import { makeStyles } from 'tss-react/mui';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ClearIcon from '@mui/icons-material/Clear';
import DownloadIcon from '@mui/icons-material/Download';
import { marked } from 'marked';
import TurndownService from 'turndown';
import { ToolLayout } from '../ToolLayout';
import { CodeEditor } from '../CodeEditor';

type Direction = 'md-to-html' | 'html-to-md';

const turndown = new TurndownService({ headingStyle: 'atx', bulletListMarker: '-', codeBlockStyle: 'fenced' });

const useStyles = makeStyles()((theme) => ({
  layout: { display: 'flex', flexDirection: 'column', gap: theme.spacing(2), height: 'calc(100vh - 260px)', minHeight: 400 },
  controls: { display: 'flex', alignItems: 'center', gap: theme.spacing(2), flexWrap: 'wrap' },
  row: { flex: 1, display: 'flex', gap: theme.spacing(2), alignItems: 'stretch', overflow: 'hidden' },
  pane: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 },
  paneHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(1),
    minHeight: 36,
  },
  paneLabel: {
    fontWeight: 600,
    fontSize: 13,
    color: theme.palette.text.secondary,
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
  },
}));

function convertMdToHtml(input: string): { result: string; error: null } | { result: null; error: string } {
  try {
    return { result: marked.parse(input) as string, error: null };
  } catch (e: any) {
    return { result: null, error: `Conversion failed: ${e?.message}` };
  }
}

function convertHtmlToMd(input: string): { result: string; error: null } | { result: null; error: string } {
  try {
    return { result: turndown.turndown(input), error: null };
  } catch (e: any) {
    return { result: null, error: `Conversion failed: ${e?.message}` };
  }
}

export function MarkdownConverterPage() {
  const { classes } = useStyles();
  const [direction, setDirection] = useState<Direction>('md-to-html');
  const [input, setInput] = useState('');
  const [output, setOutput] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const convert = useCallback((value: string, dir: Direction) => {
    if (!value.trim()) { setOutput(''); setError(null); return; }
    const { result, error: err } = dir === 'md-to-html' ? convertMdToHtml(value) : convertHtmlToMd(value);
    if (err) { setError(err); setOutput(''); }
    else { setOutput(result!); setError(null); }
  }, []);

  const handleInputChange = (value: string) => {
    setInput(value);
    convert(value, direction);
  };

  const handleDirectionChange = (_: unknown, val: Direction | null) => {
    if (!val) return;
    const newInput = output || input;
    setDirection(val);
    setInput(newInput);
    convert(newInput, val);
  };

  const SAMPLES: Record<Direction, string> = {
    'md-to-html': '# Getting Started\n\nThis is a **sample** Markdown document.\n\n## Features\n\n- Easy to read\n- Easy to write\n- Converts to HTML\n\n```js\nconsole.log(\'Hello, Backstage!\');\n```\n',
    'html-to-md': '<h1>Getting Started</h1>\n<p>This is a <strong>sample</strong> HTML document.</p>\n<h2>Features</h2>\n<ul>\n  <li>Easy to read</li>\n  <li>Easy to write</li>\n  <li>Converts to Markdown</li>\n</ul>\n<pre><code>console.log(\'Hello, Backstage!\');</code></pre>',
  };

  const clear = () => { setInput(''); setOutput(''); setError(null); setCopied(false); };

  const loadSample = () => {
    const sample = SAMPLES[direction];
    setInput(sample);
    convert(sample, direction);
  };

  const copyOutput = () => {
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const download = () => {
    const ext = direction === 'md-to-html' ? 'html' : 'md';
    const mime = direction === 'md-to-html' ? 'text/html' : 'text/markdown';
    const blob = new Blob([output], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `output.${ext}`; a.click();
    URL.revokeObjectURL(url);
  };

  const inputLabel = direction === 'md-to-html' ? 'Markdown Input' : 'HTML Input';
  const outputLabel = direction === 'md-to-html' ? 'HTML Output' : 'Markdown Output';
  const inputPlaceholder = direction === 'md-to-html'
    ? '# Hello World\n\nWrite some **Markdown** here.\n\n- Item A\n- Item B\n'
    : '<h1>Hello World</h1>\n<p>Paste your <strong>HTML</strong> here.</p>\n<ul>\n  <li>Item A</li>\n  <li>Item B</li>\n</ul>';

  return (
    <ToolLayout
      title="Markdown ↔ HTML"
      subtitle="Convert between Markdown and HTML"
    >
      <Box className={classes.layout}>
        <Box className={classes.controls}>
          <ToggleButtonGroup
            size="small"
            value={direction}
            exclusive
            onChange={handleDirectionChange}
          >
            <ToggleButton value="md-to-html" sx={{ textTransform: 'none', fontSize: 13 }}>Markdown → HTML</ToggleButton>
            <ToggleButton value="html-to-md" sx={{ textTransform: 'none', fontSize: 13 }}>HTML → Markdown</ToggleButton>
          </ToggleButtonGroup>
        </Box>

        {error && <Alert severity="error">{error}</Alert>}

        <Box className={classes.row}>
          <Box className={classes.pane}>
            <Box className={classes.paneHeader}>
              <Typography className={classes.paneLabel}>{inputLabel}</Typography>
              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                <Button size="small" variant="outlined" onClick={loadSample} sx={{ textTransform: 'none', fontSize: 12 }}>
                  Load sample
                </Button>
                <Tooltip title="Clear">
                  <span>
                    <IconButton size="small" onClick={clear} disabled={!input && !output}>
                      <ClearIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
            <CodeEditor
              value={input}
              onChange={handleInputChange}
              sx={{ flex: 1 }}
              placeholder={inputPlaceholder}
            />
          </Box>

          <Box className={classes.pane}>
            <Box className={classes.paneHeader}>
              <Typography className={classes.paneLabel}>{outputLabel}</Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <Tooltip title={copied ? 'Copied!' : 'Copy output'}>
                  <span>
                    <IconButton size="small" onClick={copyOutput} disabled={!output}>
                      <ContentCopyIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Download">
                  <span>
                    <IconButton size="small" onClick={download} disabled={!output}>
                      <DownloadIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
            <CodeEditor
              value={output}
              readOnly
              sx={{ flex: 1 }}
              placeholder="Output will appear here…"
            />
          </Box>
        </Box>
      </Box>
    </ToolLayout>
  );
}
